import { AsyncFlow, createAsyncFlow } from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';
import type { BaseRowData, DataProvider } from '../../types';

const DEFAULT_ROW_COUNT = 100;

/**
 * Метод, создающий loading-датапровайдер.
 * Loading-датапровайдер – это датапровайдер, данные которого всегда находятся в состоянии загрузки.
 * Такой датапровайдер удобно использовать для создания стаба всего грида, в то время как данные из
 * endpoint-датапровайдера еще загружаются.
 *
 * @typeParam RowData Данные строки.
 * @param count Количество элементов для отображения при загрузке.
 * @returns Провайдер данных грида.
 */
export function createLoadingDataProvider<RowData extends BaseRowData>(
    count: number = DEFAULT_ROW_COUNT
): DataProvider<RowData> {
    const rowsIds = Array.from({ length: count }, (_, index) => `not_loaded_${index}`);
    const rowsIdsSet = new Set(rowsIds);

    const pendingFlow = createAsyncFlow<RowData>({
        status: 'pending',
    }).asFlow();
    const notFoundErrorFlow = createAsyncFlow<RowData>({
        status: 'error',
        error: new Error('Row not found'),
    }).asFlow();
    const rowFlow: (id: string) => AsyncFlow<RowData> = memoize(
        (id: string) => (rowsIdsSet.has(id) ? pendingFlow : notFoundErrorFlow),
        { maxPinned: 0 }
    );

    const dataProvider: DataProvider<RowData> = {
        totalCount: createAsyncFlow({
            status: 'success',
            data: count,
        }),
        rowData: rowFlow,
        rowsIds: createAsyncFlow({
            status: 'success',
            data: rowsIds,
        }),
        loadedRowsData: createAsyncFlow({
            status: 'success',
            data: [],
        }),
    };

    return dataProvider;
}
